'use client';
import { useEffect, useRef, useState } from 'react';
import { useBreakpoint } from '../useBreakpoint';
import { SCROLL_DIRECTION } from '../../constants/commonConstants';

export const useScrollDirection = (
  breakpoint?: number | null,
  headerHeight?: number
) => {
  const [scrollDirection, setScrollDirection] = useState(SCROLL_DIRECTION.UP);
  const lastScrollY = useRef(0);
  const currentBreakpoint = useBreakpoint(breakpoint || 0);
  const [viewportThreshold, setViewportThreshold] = useState(0);

  // Only enable scroll behavior when below the breakpoint (mobile)
  // If no breakpoint provided (null/undefined), default to enabled (true)
  const shouldEnableScrollBehavior =
    breakpoint != null ? !currentBreakpoint : true;
  const scrollDownThreshold = 10;
  const scrollUpThreshold = 0;

  useEffect(() => {
    //Calculates the viewport threshold on mount and window resize

    const calculateThreshold = () => {
      if (headerHeight) {
        setViewportThreshold(headerHeight);
      } else {
        setViewportThreshold(window.innerHeight * 0.3);
      }
    };

    calculateThreshold();
    window.addEventListener('resize', calculateThreshold);

    return () => {
      window.removeEventListener('resize', calculateThreshold);
    };
  }, [headerHeight]);

  useEffect(() => {
    if (!shouldEnableScrollBehavior) return;

    const handleScroll = () => {
      const currentScrollY = window.scrollY;

      // If user is within the threshold (either headerHeight or viewport %), always show header
      if (currentScrollY <= viewportThreshold) {
        setScrollDirection(SCROLL_DIRECTION.UP);
        lastScrollY.current = currentScrollY;
        return;
      }

      // Only apply hide-on-scroll behavior after threshold
      const effectiveThreshold =
        currentScrollY > lastScrollY.current
          ? scrollDownThreshold
          : scrollUpThreshold;

      if (Math.abs(currentScrollY - lastScrollY.current) < effectiveThreshold) {
        return;
      }

      if (currentScrollY > lastScrollY.current) {
        setScrollDirection(SCROLL_DIRECTION.DOWN);
      } else if (currentScrollY < lastScrollY.current) {
        setScrollDirection(SCROLL_DIRECTION.UP);
      }
      lastScrollY.current = currentScrollY;
    };

    window.addEventListener('scroll', handleScroll, { passive: true });
    return () => {
      window.removeEventListener('scroll', handleScroll);
    };
  }, [shouldEnableScrollBehavior, viewportThreshold]);

  // Return 'UP' for devices above the breakpoint (tablet/desktop)
  return shouldEnableScrollBehavior ? scrollDirection : SCROLL_DIRECTION.UP;
};
